import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { TrendingUp, PieChart, Calendar, MessageSquare, FileText, ArrowRightLeft, Activity, Trophy, Crown, BarChart3, Edit } from 'lucide-react';

import API from '../utils/api';
import { useSyncRefresh } from '../hooks/useSyncRefresh';

// Detect if we're in preview environment
const isPreview = window.location.hostname === 'localhost' || 
                  window.location.hostname.includes('preview') ||
                  window.location.hostname.includes('127.0.0.1') ||
                  process.env.REACT_APP_BACKEND_URL?.includes('webhook-trial-debug');

var SCORE_COLORS = { Red: '#EF4444', Orange: '#F97316', Yellow: '#EAB308', Blue: '#3B82F6', Green: '#10B981' };

// Calendar color codes
var OVERNIGHT_COLORS = {
  me: '#3B82F6',        // Blue - My Scheduled Overnights
  coparent: '#EC4899',  // Pink - Co-Parent Scheduled Overnights
  me_extra: '#10B981',  // Green - My Extra/Unscheduled Overnights
  coparent_extra: '#F97316',  // Orange - Co-Parent Extra/Unscheduled Overnights
};

var ATTENDANCE_COLORS = {
  present: '#10B981',   // Green - Present
  late: '#EAB308',      // Yellow - Late
  absent: '#EF4444',    // Red - Absent
  sick: '#F97316',      // Orange - Sick
  not_in_school: '#8C86C4',  // Gray - Not in School
};

function ActivityChart({ data, range }) {
  if (!data || data.length === 0) return null;
  var sliced = data.slice(-range);
  var maxVal = Math.max.apply(null, sliced.map(function (d) { return d.total; }).concat([1]));
  return (
    <div className="card-surface p-5" data-testid="activity-chart">
      <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
        <Activity className="w-4 h-4 text-[#5A4E9E]" />
        Daily Activity ({sliced.length} days)
      </h3>
      <div className="overflow-x-auto">
        <div className="flex items-end gap-[2px] h-36" style={{ minWidth: Math.max(sliced.length * 16, 200) + 'px' }}>
          {sliced.map(function (day, i) {
            var height = day.total > 0 ? Math.max((day.total / maxVal) * 100, 8) : 4;
            var showLabel = sliced.length <= 14 || i % Math.ceil(sliced.length / 14) === 0;
            return (
              <div key={i} className="flex-1 flex flex-col items-center gap-0.5 group cursor-default relative" style={{ minWidth: '10px' }}>
                {day.total > 0 && <span className="text-[8px] text-white/30 hidden group-hover:block">{day.total}</span>}
                <div className="w-full rounded-t-sm transition-all"
                  style={{
                    height: height + '%',
                    background: day.total > 0 ? 'linear-gradient(180deg, #5A4E9E, #5A4E9E)' : 'rgba(255,255,255,0.03)',
                    minHeight: '3px', maxWidth: '16px', margin: '0 auto',
                  }}></div>
                {showLabel && <span className="text-[8px] text-white/25 whitespace-nowrap">{day.date}</span>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function PieChartComponent({ data, title, icon, colors }) {
  if (!data || data.length === 0) return null;
  
  var total = data.reduce(function(sum, item) { return sum + item.value; }, 0);
  // Show chart even with zero data for visibility
  if (total === 0) {
    return (
      <div className="card-surface p-5" data-testid={'pie-chart-' + title.toLowerCase().replace(/\s/g, '-')}>
        <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
          {icon}
          {title}
        </h3>
        <div className="flex items-center justify-center h-[200px] text-white/40 text-sm">
          No data available yet
        </div>
      </div>
    );
  }
  
  var startAngle = 0;
  var segments = data.map(function(item) {
    var percentage = (item.value / total) * 100;
    var angle = (item.value / total) * 360;
    var endAngle = startAngle + angle;
    
    // Calculate path for pie slice
    var largeArc = angle > 180 ? 1 : 0;
    var x1 = 50 + 45 * Math.cos((startAngle - 90) * Math.PI / 180);
    var y1 = 50 + 45 * Math.sin((startAngle - 90) * Math.PI / 180);
    var x2 = 50 + 45 * Math.cos((endAngle - 90) * Math.PI / 180);
    var y2 = 50 + 45 * Math.sin((endAngle - 90) * Math.PI / 180);
    
    var pathData = [
      'M', 50, 50,
      'L', x1, y1,
      'A', 45, 45, 0, largeArc, 1, x2, y2,
      'Z'
    ].join(' ');
    
    var segment = {
      path: pathData,
      color: colors[item.key] || '#3B82F6',
      percentage: percentage,
      label: item.label,
      value: item.value
    };
    
    startAngle = endAngle;
    return segment;
  });
  
  return (
    <div className="card-surface p-5" data-testid={'pie-chart-' + title.toLowerCase().replace(/\s/g, '-')}>
      <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
        {icon}
        {title}
      </h3>
      <div className="flex flex-col lg:flex-row items-center gap-6">
        {/* Pie Chart SVG */}
        <div className="relative flex-shrink-0">
          <svg width="200" height="200" viewBox="0 0 100 100" className="transform -rotate-90">
            {segments.map(function(seg, idx) {
              return (
                <path
                  key={idx}
                  d={seg.path}
                  fill={seg.color}
                  className="transition-opacity hover:opacity-80 cursor-default"
                  data-testid={'pie-segment-' + idx}
                />
              );
            })}
            {/* Center circle for donut effect */}
            <circle cx="50" cy="50" r="25" fill="rgba(28, 31, 46, 0.95)" />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <p className="text-2xl font-bold">{total}</p>
              <p className="text-xs text-white/40">Total</p>
            </div>
          </div>
        </div>
        
        {/* Legend */}
        <div className="flex-1 space-y-2 w-full">
          {data.map(function(item, idx) {
            var pct = ((item.value / total) * 100).toFixed(1);
            return (
              <div key={idx} className="flex items-center justify-between gap-3 p-2 rounded hover:bg-white/[0.03] transition-colors">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <span 
                    className="w-3 h-3 rounded-sm flex-shrink-0" 
                    style={{ backgroundColor: colors[item.key] || '#3B82F6' }}
                  ></span>
                  <span className="text-sm text-white/80 truncate">{item.label}</span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-sm font-semibold">{item.value}</span>
                  <span className="text-xs text-white/40 min-w-[45px] text-right">{pct}%</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Insights() {
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(true);
  
  // Fixed range at 30 days (1 month)
  var rangeDays = 30;

  useEffect(function () {
    axios.get(API + '/insights', { withCredentials: true })
      .then(function (res) { setData(res.data); })
      .catch(function () {})
      .finally(function () { setLoading(false); });
  }, []);

  useSyncRefresh(useCallback(function() {
    axios.get(API + '/insights', { withCredentials: true })
      .then(function (res) { setData(res.data); })
      .catch(function () {});
  }, []));

  // Redirect to dashboard if accessed in production
  if (!isPreview) {
    return (
      <div className="max-w-5xl mx-auto card-surface p-12 text-center" data-testid="insights-unavailable">
        <div className="mb-4">
          <BarChart3 className="w-16 h-16 mx-auto text-white/20" />
        </div>
        <h2 className="text-2xl font-bold mb-2">Analytics Unavailable</h2>
        <p className="text-white/60">This feature is only available in the preview environment.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto flex items-center justify-center py-24" data-testid="insights-loading">
        <div className="w-8 h-8 border-2 border-[#5A4E9E] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="max-w-5xl mx-auto card-surface p-12 text-center" data-testid="insights-error">
        <p className="text-white/60">Unable to load insights.</p>
      </div>
    );
  }

  var changeReqs = data.change_requests || {};
  var lb = data.leaderboard || {};

  return (
    <div className="space-y-5" data-testid="insights-page">
      {/* Header with Daily Tracker Button */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Analytics Dashboard</h2>
          <p className="text-sm text-white/60 mt-1">Track your co-parenting metrics and progress</p>
        </div>
        <button
          onClick={function() {
            if (window.openDailyTracker) {
              window.openDailyTracker();
            }
          }}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#5A4E9E] to-[#3B82F6] hover:from-[#4A3E8E] hover:to-[#2B72E6] rounded-xl transition-all font-medium"
          data-testid="open-daily-tracker-btn"
        >
          <Edit className="w-4 h-4" />
          Daily Tracker
        </button>
      </div>
      
      {/* Summary Cards + Leaderboard Rank */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { label: 'Messages', value: data.totals.messages, icon: MessageSquare, color: '#2D2560' },
          { label: 'Journal', value: data.totals.journal_entries, icon: FileText, color: '#5A4E9E' },
          { label: 'Events', value: data.totals.calendar_events, icon: Calendar, color: '#5A4E9E' },
          { label: 'Files', value: data.totals.case_files, icon: FileText, color: '#F97316' },
        ].map(function (card) {
          return (
            <div key={card.label} className="card-surface p-4" data-testid={'insight-total-' + card.label.toLowerCase()}>
              <div className="flex items-center gap-2 mb-2">
                <card.icon className="w-4 h-4" style={{ color: card.color }} />
                <span className="text-xs text-white/40">{card.label}</span>
              </div>
              <p className="text-2xl font-bold">{card.value}</p>
            </div>
          );
        })}
        <div className="card-surface p-4 border-[#EAB308]/20" data-testid="leaderboard-rank-card">
          <div className="flex items-center gap-2 mb-2">
            {lb.my_rank === 1 ? <Crown className="w-4 h-4 text-[#EAB308]" /> : <Trophy className="w-4 h-4 text-[#EAB308]" />}
            <span className="text-xs text-white/40">Leaderboard</span>
          </div>
          <p className={'text-2xl font-bold ' + (lb.my_rank === 1 ? 'text-[#EAB308]' : 'text-white')}>#{lb.my_rank || '—'}</p>
          <p className="text-xs text-white/30">{lb.my_points?.toLocaleString() || 0} pts / {lb.total_users || 0} users</p>
        </div>
      </div>

      {/* Activity Chart */}
      <ActivityChart data={data.daily_activity || []} range={rangeDays} />

      {/* Pie Charts Section */}
      {/* Pie Charts Section - Row 1: Schedule Baseline */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Overnights Distribution - Shows scheduled baseline from calendar */}
        <PieChartComponent
          title="Scheduled Overnights (Per Year)"
          icon={<Calendar className="w-4 h-4 text-[#3B82F6]" />}
          colors={OVERNIGHT_COLORS}
          data={[
            { key: 'me', label: 'My Scheduled', value: data.scheduled_overnights?.me || 0 },
            { key: 'coparent', label: 'Co-Parent Scheduled', value: data.scheduled_overnights?.coparent || 0 }
          ]}
        />
        
        {/* School Attendance */}
        <PieChartComponent
          title="School Attendance"
          icon={<Calendar className="w-4 h-4 text-[#10B981]" />}
          colors={ATTENDANCE_COLORS}
          data={[
            { key: 'present', label: 'On Time', value: data.attendance?.present || 0 },
            { key: 'late', label: 'Late', value: data.attendance?.late || 0 },
            { key: 'absent', label: 'Absent', value: data.attendance?.absent || 0 },
            { key: 'sick', label: 'Sick Days', value: data.attendance?.sick || 0 },
            { key: 'not_in_school', label: 'Not in School', value: data.attendance?.not_in_school || 0 }
          ]}
        />
      </div>
      
      {/* Pie Charts Section - Row 2: Compliance Tracking */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Overnight Compliance - Exercised vs Extra */}
        <PieChartComponent
          title="Overnight Compliance"
          icon={<Calendar className="w-4 h-4 text-[#5A4E9E]" />}
          colors={OVERNIGHT_COLORS}
          data={[
            { key: 'me', label: 'My Exercised', value: data.overnights_used?.me || 0 },
            { key: 'coparent', label: 'Co-Parent Exercised', value: data.overnights_used?.coparent || 0 },
            { key: 'me_extra', label: 'My Extra', value: data.overnights_extra?.me || 0 },
            { key: 'coparent_extra', label: 'Co-Parent Extra', value: data.overnights_extra?.coparent || 0 }
          ]}
        />
        
        {/* Unused Overnights */}
        <PieChartComponent
          title="Unused Overnights"
          icon={<Calendar className="w-4 h-4 text-[#EF4444]" />}
          colors={OVERNIGHT_COLORS}
          data={[
            { key: 'me', label: 'My Unused', value: data.unused_overnights?.me || 0 },
            { key: 'coparent', label: 'Co-Parent Unused', value: data.unused_overnights?.coparent || 0 }
          ]}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Journal Grades */}
        <div className="card-surface p-5" data-testid="journal-grades">
          <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-[#5A4E9E]" />
            Journal Entry Grades
          </h3>
          {Object.keys(data.journal_grades || {}).length > 0 ? (
            <div className="space-y-2">
              {['Green', 'Blue', 'Yellow', 'Orange', 'Red'].map(function (g) {
                var c = (data.journal_grades || {})[g] || 0;
                if (c === 0) return null;
                return (
                  <div key={g} className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: SCORE_COLORS[g] }}></span>
                    <span className="text-xs text-white/50 w-14">{g}</span>
                    <span className="text-sm font-semibold" style={{ color: SCORE_COLORS[g] }}>{c}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-white/30 text-sm">No journal entries yet.</p>
          )}
        </div>

        {/* Change Request Stats */}
        <div className="card-surface p-5" data-testid="change-request-stats">
          <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
            <ArrowRightLeft className="w-4 h-4 text-[#10B981]" />
            Calendar Change Requests
          </h3>
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Sent', value: changeReqs.sent || 0, color: '#5A4E9E' },
              { label: 'Received', value: changeReqs.received || 0, color: '#2D2560' },
              { label: 'Approved (Sent)', value: changeReqs.approved_sent || 0, color: '#10B981' },
              { label: 'Approved (Received)', value: changeReqs.approved_received || 0, color: '#10B981' },
            ].map(function (stat) {
              return (
                <div key={stat.label} className="p-3 rounded-lg border border-white/5 bg-white/[0.02]">
                  <p className="text-lg font-bold" style={{ color: stat.color }}>{stat.value}</p>
                  <p className="text-xs text-white/40">{stat.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Insights;
